import { RoleItem } from "@/domain/rol.interface";
import { instanceBo } from "../utils/axios.create";
import {
	IUser,
	IUserDelete,
	ParamsDelete,
	IUserPayload,
} from "@/domain/user.interface";
import { IAction } from "@/domain/action.interface";

const findAll = async (): Promise<IUser[]> => {
	return instanceBo
		.get("/users")
		.then((res) => {
			return res.data.body;
		})
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const deleteOne = async (params: ParamsDelete): Promise<IUserDelete> => {
	// SIMS-6658: repointed v1 axios DELETE -> v2 instanceBo DELETE (path already
	// plural /users/:id). Network-error guard mirrors the other v2 calls.
	return instanceBo
		.delete(`/users/${params.userId}`)
		.then((res) => {
			return res.data.body;
		})
		.catch((err) => {
			if (!err.response) {
				return Promise.reject({
					error: "NetworkError",
					message: err.message || "Network error occurred",
					code: err.code,
				});
			}
			return Promise.reject(err.response.data);
		});
};

const findById = async (userId: string): Promise<IUser> => {
	return instanceBo
		.get(`/users/${userId}`)
		.then((res) => {
			return res.data.body;
		})
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export interface IRoleAction extends Omit<RoleItem, "actions" | "level"> {
	_id: string;
	actions: IAction[];
}
export interface IUserRole extends Omit<IUser, "roles"> {
	roles: IRoleAction[];
}

const findByEmail = async (
	email: string,
	subIdp: string,
): Promise<IUserRole> => {
	return instanceBo
		.get("/users/user-info", { params: { email, subIdp } })
		.then((res) => {
			return res.data.body;
		})
		.catch((err) => {
			// Handle CORS errors and network errors where response is undefined
			if (!err.response) {
				return Promise.reject({
					error: "NetworkError",
					message: err.message || "Network error occurred",
					code: err.code,
				});
			}
			return Promise.reject(err.response.data);
		});
};

const updateById = async (
	data: Partial<IUserPayload>,
	userId: string,
): Promise<IUserPayload> => {
	// SIMS-6642: repointed v1 axios PUT /user/:id -> v2 instanceBo PUT /users/:id
	// (plural, consistent with the migrated findById). Network-error guard mirrors
	// findByEmail/create so a CORS/offline failure rejects with a typed error.
	return instanceBo
		.put(`/users/${userId}`, data)
		.then((res) => res.data.body)
		.catch((err) => {
			if (!err.response) {
				return Promise.reject({
					error: "NetworkError",
					message: err.message || "Network error occurred",
					code: err.code,
				});
			}
			return Promise.reject(err.response.data);
		});
};

const create = async (data: IUserPayload): Promise<IUser> => {
	return instanceBo
		.post("/users", data)
		.then((res) => res.data.body)
		.catch((err) => {
			// Guard the network/CORS case (err.response undefined) so we don't
			// throw an opaque TypeError on a failed create — mirrors findByEmail.
			if (!err.response) {
				return Promise.reject({
					error: "NetworkError",
					message: err.message || "Network error occurred",
					code: err.code,
				});
			}
			return Promise.reject(err.response.data);
		});
};

export const userService = {
	findAll,
	deleteOne,
	findById,
	findByEmail,
	updateById,
	create,
};
